'use client';

import type { BadgeProps } from '@petsmart-ui/sparky';
import { Badge, Card, SparkyTextWrapper, TextPassage } from '@petsmart-ui/sparky';
import classnames from 'classnames';
import { usePathname, useSearchParams } from 'next/navigation';
import type { ReactElement } from 'react';

import { dataLayerPush } from '@libs/analytics';
import { useDecisions } from '@libs/common-next';
import type { Offer } from '@libs/common-types';

import { getActivateRewardSelection } from './autoshipPointsPayoutTracking';
import { OfferCardButton } from './OfferCardButton';
import { useOfferCardActions } from './useOfferCardActions';
import './OfferCard.scss';

type CardSize = 'featured' | 'large' | 'small';

function getCardSize(size: CardSize): {
    isFeatured: boolean;
    isLarge: boolean;
    isSmall: boolean;
} {
    return {
        isFeatured: size === 'featured',
        isLarge: size === 'large',
        isSmall: size === 'small',
    };
}

/** ------------------------------------
 * component
 * ---------------------------------- */

export interface OfferCardProps {
    // TODO: will it be set on the offer in Contentful?  If so, move to and update Offer below
    badgeText?: BadgeProps['text'];
    // TODO: will it be set on the offer in Contentful?  If so, move to and update Offer below
    badgeVariant?: BadgeProps['variant'];
    className?: string;
    offer: Offer;
    size?: CardSize;
}

export function OfferCard({ badgeText, badgeVariant, className, offer, size = 'small' }: OfferCardProps): ReactElement {
    const { handleActivateOffer, showSpinner, handleCardClick } = useOfferCardActions();
    const isAsPointsPayoutCtaEnabled = useDecisions('as_pts_pyt_cta');
    const pathname = usePathname();
    const searchParams = useSearchParams();
    const activateOfferIdFromUrl = searchParams?.get('activateOfferId');

    const baseClassName = 'c-offer-card';
    const componentClassName = classnames(baseClassName, className, {
        [`${baseClassName}-${size}`]: size,
    });

    const { isFeatured, isLarge, isSmall } = getCardSize(size);
    const expiration = `Exp ${offer.expiration?.title}. ${offer.expiration?.description}`;
    const showButton = offer.isActivatable || offer.isActivated;
    const siteSection = pathname?.includes('offers') ? 'offers' : 'treats rewards';

    return (
        <Card
            className={componentClassName}
            tabIndex={0}
            onClick={() => handleCardClick(offer)}
            data-testid="offer-card"
        >
            {(isFeatured || isLarge) && (
                <Card.Header>
                    <img
                        src={isFeatured ? offer.webImageUrlFeatured : offer.webImageUrlLarge}
                        alt={offer.shortDescription}
                    />
                </Card.Header>
            )}
            <Card.Body>
                {isSmall ? (
                    <div className="offer-card-content">
                        <img src={offer.webImageUrl} alt={offer.shortDescription} />

                        <div className="offer-card-content-inner">
                            <SparkyTextWrapper truncateOn={3}>
                                <TextPassage capLinelength={false}>
                                    <strong>{offer.shortDescription}</strong>
                                </TextPassage>
                            </SparkyTextWrapper>

                            {badgeText && <Badge variant={badgeVariant} text={badgeText} />}
                        </div>
                    </div>
                ) : (
                    <div className="offer-card-content">
                        <SparkyTextWrapper truncateOn={2}>
                            <TextPassage capLinelength={false} size={isFeatured ? 'lg' : undefined}>
                                <strong>{offer.shortDescription}</strong>
                            </TextPassage>
                        </SparkyTextWrapper>
                        <SparkyTextWrapper truncateOn={2}>
                            <TextPassage className="offer-long-description" capLinelength={false}>
                                {offer.longDescription}
                            </TextPassage>
                        </SparkyTextWrapper>
                    </div>
                )}

                <Card.Footer>
                    {offer.expiration && <TextPassage size="sm">{expiration}</TextPassage>}
                    {showButton && (
                        <OfferCardButton
                            fullWidth={!!isFeatured}
                            isActivated={offer.isActivated}
                            loading={showSpinner(offer)}
                            onClick={e => {
                                const element = e.currentTarget as HTMLElement;
                                e.stopPropagation();
                                handleActivateOffer(offer, siteSection);
                                element.blur();

                                if (!offer.isActivated) {
                                    dataLayerPush({
                                        event: 'activate_reward',
                                        page_section: siteSection,
                                        selection: getActivateRewardSelection({
                                            offer,
                                            isAsPointsPayoutCtaEnabled,
                                            activateOfferIdFromUrl,
                                        }),
                                    });
                                }
                            }}
                            size={isFeatured ? 'sm' : undefined}
                        />
                    )}
                </Card.Footer>
            </Card.Body>
        </Card>
    );
}
